import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { codexApi } from '../api';
import { celebrate } from '../../lib/celebrate';
import { Scroll, Shield, Crown, Eye, Sword, Footprints, ChevronRight } from 'lucide-react';

const AVATARS = [
  { id: 'scribe',    name: 'The Scribe',    icon: Scroll,     desc: 'Lover of the Word. Patient. Precise.' },
  { id: 'shepherd',  name: 'The Shepherd',  icon: Footprints, desc: 'Servant-hearted. Careful with the flock.' },
  { id: 'watchman',  name: 'The Watchman',  icon: Eye,        desc: 'Discerning. Sees what others miss.' },
  { id: 'warrior',   name: 'The Warrior',   icon: Sword,      desc: 'Bold. Stands when others sit.' },
  { id: 'prophet',   name: 'The Prophet',   icon: Crown,      desc: 'Hears Heaven. Speaks fire.' },
];

const PACES = [
  { id: 'casual',  name: 'Casual',  desc: '1 layer per day — gentle, sustainable.' },
  { id: 'devoted', name: 'Devoted', desc: 'Full scroll daily — the disciple\'s rhythm.' },
  { id: 'zealous', name: 'Zealous', desc: 'Full scroll + bonus reflection — for the hungry.' },
];

export default function CodexOnboarding({ onComplete }) {
  const [step, setStep] = useState(0);
  const [avatar, setAvatar] = useState(null);
  const [pace, setPace] = useState('devoted');
  const [submitting, setSubmitting] = useState(false);

  const finish = async () => {
    if (!avatar) return;
    setSubmitting(true);
    try {
      await codexApi.onboard(avatar, pace);
      celebrate(0.5);
      setTimeout(() => onComplete(), 600);
    } catch (e) {
      alert(e.message);
      setSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
      <div style={{ width: '100%', maxWidth: 720 }}>
        <div className="codex-eyebrow" style={{ textAlign: 'center', marginBottom: 16 }}>
          STEP {step + 1} OF 2
        </div>

        {step === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="codex-card"
            style={{ padding: 32 }}
            data-testid="codex-onboard-avatar-step"
          >
            <h1 className="codex-h1" style={{ fontSize: '2rem', marginBottom: 8 }}>Choose Your Path</h1>
            <p style={{ opacity: 0.75, marginBottom: 24, fontSize: 14 }}>
              An archetype to walk with on this sacred journey. You can change later.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
              {AVATARS.map((a) => {
                const Icon = a.icon;
                const active = avatar === a.id;
                return (
                  <motion.button
                    key={a.id}
                    whileHover={{ y: -3 }}
                    onClick={() => setAvatar(a.id)}
                    data-testid={`codex-avatar-${a.id}`}
                    className="codex-card"
                    style={{
                      padding: 18,
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderColor: active ? 'rgba(212,175,55,0.7)' : undefined,
                      background: active ? 'linear-gradient(180deg, rgba(212,175,55,0.18), rgba(168,85,247,0.1))' : undefined,
                      color: 'var(--codex-parchment)',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <Icon size={26} color={active ? 'var(--codex-gold)' : 'var(--codex-gold-light)'} />
                    <div className="codex-display" style={{ marginTop: 10, fontSize: 18, color: 'var(--codex-gold-light)' }}>{a.name}</div>
                    <div style={{ fontSize: 12, opacity: 0.75, marginTop: 4 }}>{a.desc}</div>
                  </motion.button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
              <button
                disabled={!avatar}
                onClick={() => setStep(1)}
                className="codex-btn codex-btn-primary"
                style={{ opacity: avatar ? 1 : 0.4 }}
                data-testid="codex-onboard-next"
              >
                Continue <ChevronRight size={16} />
              </button>
            </div>
          </motion.div>
        )}

        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="codex-card"
            style={{ padding: 32 }}
            data-testid="codex-onboard-pace-step"
          >
            <h1 className="codex-h1" style={{ fontSize: '2rem', marginBottom: 8 }}>Choose Your Pace</h1>
            <p style={{ opacity: 0.75, marginBottom: 24, fontSize: 14 }}>
              The Spirit walks with you — set a rhythm you can keep.
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              {PACES.map((p) => {
                const active = pace === p.id;
                return (
                  <motion.button
                    key={p.id}
                    whileHover={{ x: 4 }}
                    onClick={() => setPace(p.id)}
                    data-testid={`codex-pace-${p.id}`}
                    className="codex-card"
                    style={{
                      padding: 18,
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderColor: active ? 'rgba(212,175,55,0.7)' : undefined,
                      background: active ? 'linear-gradient(90deg, rgba(212,175,55,0.18), rgba(168,85,247,0.1))' : undefined,
                      color: 'var(--codex-parchment)',
                    }}
                  >
                    <div className="codex-display" style={{ fontSize: 18, color: 'var(--codex-gold-light)' }}>{p.name}</div>
                    <div style={{ fontSize: 13, opacity: 0.8, marginTop: 4 }}>{p.desc}</div>
                  </motion.button>
                );
              })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
              <button onClick={() => setStep(0)} className="codex-btn codex-btn-ghost">Back</button>
              <button
                disabled={submitting}
                onClick={finish}
                className="codex-btn codex-btn-primary"
                data-testid="codex-onboard-finish"
              >
                {submitting ? 'Sealing…' : 'Begin Journey'} <Shield size={16} />
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
